import { leaksBannerBig } from "#web/assets";
import { FeedbackModal } from "#web/components/feedback/FeedbackModal";
import { LeakedCodeCountsQuery } from "#web/components/leaks/LeakedCodeCountsQuery";
import { LeakedCodePageQuery, LeakedCodePageQueryProps } from "#web/components/leaks/LeakedCodePageQuery";
import { LeakedCodesIndexTable } from "#web/components/leaks/LeakedCodesIndexTable/LeakedCodesIndexTable";
import { useExportLeakedCodes } from "#web/components/leaks/useExportLeakedCodes";
import { makeShop, ShopQuery } from "#web/components/shop/queries/shopQuery";
import { routeConfig } from "#web/configs/route";
import { i18nLike } from "#web/services/i18nLike";
import { debounce } from "#web/utils/debounce";
import { getCachedDateOfNow } from "#web/utils/getCachedDateOfNow";
import { LeakedCodeFilter } from "@gadget-client/keepcart-server";
import { useAppBridge } from "@shopify/app-bridge-react";
import {
  Badge,
  Banner,
  Box,
  Button,
  Card,
  Frame,
  Icon,
  InlineStack,
  Layout,
  Page,
  TabProps,
  Toast,
  Tooltip,
} from "@shopify/polaris";
import { ExportIcon, InfoIcon, LightbulbIcon, SettingsIcon } from "@shopify/polaris-icons";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const locale = {
  alertEmailsBanner: {
    buttonLabel: "Go to settings",
    postButtonLabel: "to set an email address to get alerted on coupon leaks.",
    preButtonLabel: "Go to ",
    title: "Alert email address missing",
  },
  backAction: "Dashboard",
  error: {
    leakedCodesQuery: {
      content: "Please try again later",
      title: "Error fetching leaked codes",
    },
  },
  export: {
    accessibilityLabel: "Export leaked codes",
    content: "Export",
    error: "Error exporting leaked codes",
    loading: "Exporting leaked codes",
    success: "Leaked codes export ready",
  },
  feedback: {
    accessibilityLabel: "Give feedback",
    content: "Feedback",
  },
  freemiumBanner: {
    buttonLabel: "Upgrade",
    postButtonLabel: "to get daily coupon leak scanning.",
    title: "You're Freemium. Only one scan per month",
  },
  latestScannedAt: "Scanned {{distance}}",
  relative: {
    lessThanSecondAgo: "less than a second ago",
  },
  subtitle: "Get alerted when your code leaks on a coupon website. Code values are updated every scan.",
  tab: {
    couponExtensionLeakedCodes: {
      title: "Coupon Extension",
    },
    highRiskLeakedCodes: {
      title: "High Risk",
    },
    leakedCodes: {
      title: "Leaked",
      tooltip: "This code is currently leaked on coupon sites",
    },
    notLeakedCodes: {
      title: "Not Leaked",
      tooltip: "This code is currently not leaked on coupon sites",
    },
  },
  title: "Coupon Leak Scanner",
} as const;

const LEAKS_INFO_BANNER_KEY = "leaksInfoBannerDismissed";

/**
 * Determines the time since the last scan based on the user's plan type
 *
 * Scan frequencies:
 *
 * - Freemium: Monthly scan on the 1st of each month
 * - Paid plans: Scans every 8 hours (at 00:00, 08:00, and 16:00 UTC)
 *
 * @param isFreemium - Whether the user is on the freemium plan
 * @param installDate - The date when the app was installed
 *
 * @returns A human-readable string indicating how long ago the last scan occurred
 */
const determineLatestScannedAt = (isFreemium: boolean, installDate: Date) => {
  const currentDate = getCachedDateOfNow();

  if (isFreemium) {
    const firstOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const lastScanDate = firstOfMonth.getTime() < installDate.getTime() ? installDate : firstOfMonth;
    const timeSinceLastScan = Math.abs(currentDate.getTime() - lastScanDate.getTime()) / 1000;
    return formatRelativeTime(timeSinceLastScan);
  }

  const scanScheduleUTC = [0, 8, 16] as const;
  const currentUTCHour = currentDate.getUTCHours();

  const lastCompletedScanHour =
    [...scanScheduleUTC].reverse().find((hour: number) => hour <= currentUTCHour) ??
    scanScheduleUTC[scanScheduleUTC.length - 1]!;

  const lastScanDate = new Date(currentDate);
  lastScanDate.setUTCHours(lastCompletedScanHour, 0, 0, 0);

  if (lastScanDate > currentDate) {
    lastScanDate.setUTCDate(lastScanDate.getUTCDate() - 1);
  }

  const adjustedLastScanDate = lastScanDate.getTime() < installDate.getTime() ? installDate : lastScanDate;
  const timeSinceLastScan = Math.abs(currentDate.getTime() - adjustedLastScanDate.getTime()) / 1000;
  return formatRelativeTime(timeSinceLastScan);
};

/**
 * Formats a time difference into a human-readable relative time string
 *
 * @param seconds - The time difference in seconds
 *
 * @returns A human-readable string like "2 hours ago" or "3 days ago"
 */
const formatRelativeTime = (seconds: number): string => {
  const rtf = new Intl.RelativeTimeFormat("en-GB", { numeric: "auto" });

  const timeUnits = {
    day: 86400,
    hour: 3600,
    minute: 60,
    month: 2592000,
    second: 1,
    year: 31536000,
  } as const;

  for (const [unit, secondsInUnit] of Object.entries(timeUnits)) {
    const unitsAgo = Math.floor(seconds / secondsInUnit);
    if (unitsAgo >= 1) {
      return rtf.format(-unitsAgo, unit as Intl.RelativeTimeFormatUnit);
    }
  }

  return "less than a second ago";
};

export const Leaks = () => {
  const t = i18nLike(locale);
  const navigate = useNavigate();
  const location = useLocation();
  const { data: shopData, isError: shopError, isFetching: shopFetching } = useQuery(ShopQuery());
  const shop = shopData ? makeShop(shopData) : null;
  const shopify = useAppBridge();

  const isFreemium = !shop?.subscription ? false : shop.subscription.type === "freemium";

  const isSubscribedToAlertEmails =
    !isFreemium && shop?.alertEmails && Array.isArray(shop.alertEmails) && shop.alertEmails.length > 0;
  const latestScannedAt = determineLatestScannedAt(isFreemium, shop?.createdAt ?? getCachedDateOfNow());

  const isInitialScrapingComplete = shop?.initialScrapeStatus !== "scraping";

  const {
    data: leakedCodesCountsData,
    isError: leakedCodesCountsError,
    isFetching: leakedCodesCountsFetching,
  } = useQuery(LeakedCodeCountsQuery());

  // @see  `isCurrentlyLeaked` computed field in gadget schema
  const consideredLeakedAfterDays = shop?.subscription?.id === "23" ? 35 : 5;
  const activeLeakedCodesFilter = {
    AND: [
      {
        lastScrapedAt: {
          greaterThanOrEqual: new Date(
            getCachedDateOfNow().getTime() - 1000 * 60 * 60 * 24 * consideredLeakedAfterDays,
          ),
        },
      },
      {
        shopifyDiscountCodeId: {
          isSet: true,
        },
      },
    ],
  } satisfies LeakedCodeFilter;
  const [leakedCodesQuery, setLeakedCodesQuery] = useState<LeakedCodePageQueryProps>({
    after: null,
    before: null,
    filter: {
      ...activeLeakedCodesFilter,
    },
    search: null,
  });
  const {
    data: leakedCodes,
    isError: leakedCodesError,
    isFetching: leakedCodesFetching,
  } = useQuery(LeakedCodePageQuery(leakedCodesQuery));

  const [queryValue, setQueryValue] = useState<string>("");
  const handleQueryChange = (value: string) => {
    setQueryValue(value);
  };
  const handleQueryClear = () => {
    setQueryValue("");
  };
  const updateQueryOnSearchChange = useCallback(
    debounce((value: string) => {
      setLeakedCodesQuery({
        ...leakedCodesQuery,
        search: value.length > 0 ? value : null,
      });
    }, 250),
    [leakedCodesQuery],
  );
  useEffect(() => {
    updateQueryOnSearchChange(queryValue);
  }, [queryValue]);

  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0);
  const selectTabById = (id: string) => {
    setSelectedTabIndex(tabs.findIndex((tab) => tab.id === id));
  };

  const formatBadgeCount = (count: number) => {
    return count > 999 ? "999+" : String(count);
  };

  const tabs: TabProps[] = [
    {
      badge: leakedCodesCountsData ? formatBadgeCount(leakedCodesCountsData.active) : "..",
      content: t("tab.leakedCodes.title"),
      id: "active-leaked-codes",
      onAction: () => {
        selectTabById("active-leaked-codes");
        setLeakedCodesQuery({
          ...leakedCodesQuery,
          after: null,
          before: null,
          filter: activeLeakedCodesFilter,
        });
      },
    },
    {
      // @ts-expect-error type 'Element' should not be allowed here but it does work so we're just going to ignore it
      // also we cannot put the tooltip in the content because it breaks when toggling index filters search
      // @see https://github.com/Shopify/polaris/blob/6474119cc73317f92e4c20db75b7ecc5cd5249ad/polaris-react/src/components/IndexFilters/components/UpdateButtons/UpdateButtons.tsx#L96
      badge: (
        <InlineStack gap="100">
          <span>{leakedCodesCountsData ? formatBadgeCount(leakedCodesCountsData.inactive) : ".."}</span>
          <Tooltip content={t("tab.notLeakedCodes.tooltip")}>
            <Icon source={InfoIcon} />
          </Tooltip>
        </InlineStack>
      ),
      content: t("tab.notLeakedCodes.title"),
      id: "not-leaked-codes",
      onAction: () => {
        selectTabById("not-leaked-codes");
        setLeakedCodesQuery({
          ...leakedCodesQuery,
          after: null,
          before: null,
          filter: {
            AND: [
              {
                NOT: [
                  {
                    lastScrapedAt: {
                      greaterThanOrEqual: new Date(
                        getCachedDateOfNow().getTime() - 1000 * 60 * 60 * 24 * consideredLeakedAfterDays,
                      ),
                    },
                  },
                ],
              },
              {
                shopifyDiscountCodeId: {
                  isSet: true,
                },
              },
            ],
          },
        });
      },
    },
    {
      badge: leakedCodesCountsData ? formatBadgeCount(leakedCodesCountsData.highRisk) : "..",
      content: t("tab.highRiskLeakedCodes.title"),
      id: "high-risk-leaked-codes",
      onAction: () => {
        selectTabById("high-risk-leaked-codes");
        setLeakedCodesQuery({
          ...leakedCodesQuery,
          after: null,
          before: null,
          // @see `isHighRisk` computed field in gadget schema
          filter: {
            OR: [
              {
                AND: [
                  {
                    OR: [
                      {
                        AND: [
                          {
                            value: {
                              greaterThanOrEqual: 0,
                            },
                          },
                          {
                            value: {
                              greaterThanOrEqual: shop?.highRiskCodePercentageMinimum ?? 0,
                            },
                          },
                        ],
                      },
                      {
                        AND: [
                          {
                            value: {
                              lessThanOrEqual: 0,
                            },
                          },
                          {
                            value: {
                              lessThanOrEqual: (shop?.highRiskCodePercentageMinimum ?? 0) * -1,
                            },
                          },
                        ],
                      },
                    ],
                    valueType: {
                      equals: "percentage",
                    },
                  },
                ],
              },
              {
                AND: [
                  {
                    OR: [
                      {
                        AND: [
                          {
                            value: {
                              greaterThanOrEqual: 0,
                            },
                          },
                          {
                            value: {
                              greaterThanOrEqual: shop?.highRiskCodeCurrencyMinimum ?? 0,
                            },
                          },
                        ],
                      },
                      {
                        AND: [
                          {
                            value: {
                              lessThanOrEqual: 0,
                            },
                          },
                          {
                            value: {
                              lessThanOrEqual: (shop?.highRiskCodeCurrencyMinimum ?? 0) * -1,
                            },
                          },
                        ],
                      },
                    ],
                    valueType: {
                      notEquals: "percentage",
                    },
                  },
                ],
              },
            ],
          },
        });
      },
    },
    {
      badge: leakedCodesCountsData ? formatBadgeCount(leakedCodesCountsData.couponExtension) : "..",
      content: t("tab.couponExtensionLeakedCodes.title"),
      id: "coupon-extension-leaked-codes",
      onAction: () => {
        selectTabById("coupon-extension-leaked-codes");
        setLeakedCodesQuery({
          ...leakedCodesQuery,
          after: null,
          before: null,
          filter: {
            leakSources: {
              matches: { "Coupon Extension": shop?.domain ?? "" },
            },
          },
        });
      },
    },
  ];
  useEffect(() => {
    const maybeSetInitialTab = () => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (location.state?.tab === "highrisk") {
        selectTabById("high-risk-leaked-codes");
      }
    };

    maybeSetInitialTab();
  }, []);

  const [activeToast, setActiveToast] = useState<string[]>([]);
  const [{ data: exportData, error: exportError, loading: exportLoading }, exportLeakedCodes] = useExportLeakedCodes({
    currency: shop?.currency ?? "",
  });
  useEffect(() => {
    if (exportData) {
      setActiveToast(["export-success"]);
    }
    if (exportLoading) {
      setActiveToast(["export-loading"]);
    }
    if (exportError) {
      setActiveToast(["export-error"]);
    }
  }, [exportData, exportLoading, exportError]);

  const isLoading = leakedCodesCountsFetching || leakedCodesFetching || shopFetching;
  const isError = leakedCodesCountsError || leakedCodesError || shopError;

  const [showLeaksInfoBanner, setShowLeaksInfoBanner] = useState(() => {
    if (typeof window === "undefined") return true;
    return !localStorage.getItem(LEAKS_INFO_BANNER_KEY);
  });

  useEffect(() => {
    if (!showLeaksInfoBanner) localStorage.setItem(LEAKS_INFO_BANNER_KEY, "1");
  }, [showLeaksInfoBanner]);

  return (
    <Frame>
      <div style={{ margin: "auto", maxWidth: "80rem" }}>
        <Page
          backAction={{
            content: t("backAction"),
            onAction: () => {
              navigate(routeConfig["/"]);
            },
          }}
          fullWidth={true}
          secondaryActions={[
            {
              accessibilityLabel: t("export.accessibilityLabel"),
              content: t("export.content"),
              icon: ExportIcon,
              id: "export-leaked-codes",
              onAction: () => {
                exportLeakedCodes();
              },
            },
            {
              accessibilityLabel: t("feedback.accessibilityLabel"),
              content: t("feedback.content"),
              icon: LightbulbIcon,
              id: "feedback",
              onAction: () => {
                void shopify.modal.show("feedback-modal");
              },
            },
            {
              accessibilityLabel: "Settings",
              content: "",
              icon: SettingsIcon,
              id: "settings",
              onAction: () => {
                navigate(routeConfig.settings["/"], { state: { tab: "alerts" } });
              },
            },
          ]}
          subtitle={t("subtitle")}
          title={t("title")}
          titleMetadata={
            <Badge>
              {/* @ts-expect-error type 'Element' should not be allowed here but it does work so we're just going to ignore it */}
              <>
                <div className="notification-dot" style={{ marginRight: "0.5rem" }}>
                  <div className="ping-effect notification-dot"></div>
                </div>
                {t("latestScannedAt", { distance: latestScannedAt })}
              </>
            </Badge>
          }
        >
          {isError ? (
            <Banner title={t("error.leakedCodesQuery.title")} tone="critical">
              {t("error.leakedCodesQuery.content")}
            </Banner>
          ) : (
            <Layout>
              <Layout.Section variant="fullWidth">
                <Box>
                  <img src={leaksBannerBig} style={{ borderRadius: "0.5rem", width: "100%" }} />
                </Box>
              </Layout.Section>
              {showLeaksInfoBanner && (
                <Layout.Section variant="fullWidth">
                  <Banner tone="info" onDismiss={() => setShowLeaksInfoBanner(false)}>
                    <span>
                      <b>Note:</b> This is not a list of codes that we blocked; these are leaks we found scanning coupon
                      sites/extensions. To see blocked codes go to{" "}
                      <Button
                        variant="plain"
                        onClick={() => navigate(routeConfig.extensions["/"])}
                        accessibilityLabel="Go to Extension Blocking"
                      >
                        Extension Blocking
                      </Button>
                      .
                    </span>
                  </Banner>
                </Layout.Section>
              )}
              {isFreemium && (
                <Layout.Section variant="oneHalf">
                  <Banner title={t("freemiumBanner.title")}>
                    <span>
                      <Button
                        onClick={() => {
                          navigate(routeConfig.billing["/"]);
                        }}
                        variant="plain"
                      >
                        {t("freemiumBanner.buttonLabel")}
                      </Button>{" "}
                      <span>{t("freemiumBanner.postButtonLabel")}</span>
                    </span>
                  </Banner>
                </Layout.Section>
              )}
              {!isFreemium && !isSubscribedToAlertEmails && (
                <Layout.Section variant="oneHalf">
                  <Banner title={t("alertEmailsBanner.title")} tone="info">
                    <span>
                      <Button
                        onClick={() => {
                          navigate(routeConfig.settings["/"], {
                            state: { tab: "alerts" },
                          });
                        }}
                        variant="plain"
                      >
                        {t("alertEmailsBanner.buttonLabel")}
                      </Button>
                    </span>{" "}
                    <span>{t("alertEmailsBanner.postButtonLabel")}</span>
                  </Banner>
                </Layout.Section>
              )}
              <Layout.Section variant="fullWidth">
                {shop?.bulkOperations.edges.some((edge) => edge.node.status !== "completed") && (
                  <Box paddingBlockEnd="200">
                    <Card padding="0">
                      <Banner title="Code usage syncing...">
                        <p>Date will appear soon - This may take a few mins or up to 24 hours.</p>
                      </Banner>
                    </Card>
                  </Box>
                )}
                <LeakedCodesIndexTable
                  isInitialScrapingComplete={isInitialScrapingComplete}
                  leakedCodes={leakedCodes?.leakedCodes ?? []}
                  loading={isLoading}
                  onQueryChange={handleQueryChange}
                  onQueryClear={handleQueryClear}
                  pagination={{
                    hasNext: leakedCodes?.hasNextPage ?? false,
                    hasPrevious: leakedCodes?.hasPreviousPage ?? false,
                    onNext: () => {
                      setLeakedCodesQuery({
                        ...leakedCodesQuery,
                        after: leakedCodes?.endCursor ?? null,
                        before: null,
                      });
                    },
                    onPrevious: () => {
                      setLeakedCodesQuery({
                        ...leakedCodesQuery,
                        after: null,
                        before: leakedCodes?.startCursor ?? null,
                      });
                    },
                  }}
                  queryValue={queryValue}
                  selected={selectedTabIndex}
                  shopCurrency={shop?.currency ?? ""}
                  tabs={tabs}
                />
              </Layout.Section>
            </Layout>
          )}
          <FeedbackModal />
        </Page>
      </div>
      {activeToast.includes("export-success") && (
        <Toast
          content={t("export.success")}
          duration={1000 * 5}
          key={"export-success"}
          onDismiss={() => {
            setActiveToast(activeToast.filter((toast) => toast !== "export-success"));
          }}
        />
      )}
      {activeToast.includes("export-loading") && (
        <Toast
          content={t("export.loading")}
          onDismiss={() => {
            setActiveToast(activeToast.filter((toast) => toast !== "export-loading"));
          }}
        />
      )}
      {activeToast.includes("export-error") && (
        <Toast
          content={t("export.error")}
          duration={1000 * 5}
          error={true}
          onDismiss={() => {
            setActiveToast(activeToast.filter((toast) => toast !== "export-error"));
          }}
        />
      )}
    </Frame>
  );
};
